import { supabase } from "./supabaseClient";

export const WORKOUT_GROUP_PLAN_ORDER = {
  free: 0,
  coach_starter: 1,
  coach: 2,
  pro_coach: 3,
  team_club: 4,
};

const FREE_LIMITS = { groups: 1, athletes_per_group: 2, assigned_workouts_per_group: 1, ranges: [7] };
const TIER_LIMITS = {
  free: FREE_LIMITS,
  coach_starter: { groups: 2, athletes_per_group: 6, assigned_workouts_per_group: 2, ranges: [7, 14, 30] },
  coach: { groups: 3, athletes_per_group: 12, assigned_workouts_per_group: 5, ranges: [7, 14, 30] },
  pro_coach: { groups: null, athletes_per_group: null, assigned_workouts_per_group: null, ranges: [7, 14, 30] },
  team_club: { groups: null, athletes_per_group: null, assigned_workouts_per_group: null, ranges: [7, 14, 30] },
};

const tierRank = (tier) => WORKOUT_GROUP_PLAN_ORDER[tier || "free"] ?? 0;

export const hasWorkoutGroupTier = (access, tier) =>
  tierRank(access?.tier) >= (WORKOUT_GROUP_PLAN_ORDER[tier] ?? 0);

export const hasWorkoutGroupEntitlement = (access, key) =>
  access?.entitlements?.has?.(key) === true;

export const getDefaultWorkoutGroupAccess = () => ({
  tier: "free",
  planName: "Workout Groups Free",
  source: "free",
  status: "active",
  expiresAt: null,
  renewsAt: null,
  limits: FREE_LIMITS,
  entitlements: new Set(["workout_groups.dashboard.basic"]),
  products: [],
  prices: [],
  activeEntitlements: [],
  activeSubscription: null,
});

const isCurrentlyActive = (row) => {
  if (!["active", "trialing"].includes(row?.status)) return false;
  if (!row?.expires_at) return true;
  return new Date(row.expires_at).getTime() > Date.now();
};

export const loadWorkoutGroupAccess = async (profileId) => {
  const base = getDefaultWorkoutGroupAccess();
  if (!profileId) return base;

  const [
    { data: products },
    { data: prices },
    { data: entitlements },
    { data: subscriptions },
  ] = await Promise.all([
    supabase.from("billing_products").select("*").eq("product_line", "workout_groups").eq("active", true),
    supabase
      .from("billing_prices")
      .select("*, billing_products!inner(product_line, product_key, name, metadata)")
      .eq("active", true)
      .eq("billing_products.product_line", "workout_groups"),
    supabase
      .from("customer_entitlements")
      .select("*, entitlement_definitions(entitlement_key,name,product_line), billing_products(product_key,name,product_line,metadata), billing_prices(price_key,amount_cents,coin_amount,interval,metadata)")
      .eq("profile_id", profileId),
    supabase
      .from("billing_subscriptions")
      .select("*, billing_products(product_key,name,metadata), billing_prices(price_key,amount_cents,currency,interval)")
      .eq("profile_id", profileId)
      .in("status", ["trialing", "active", "past_due"])
      .order("created_at", { ascending: false }),
  ]);

  const activeEntitlements = (entitlements || []).filter((row) => {
    const key = row?.entitlement_definitions?.entitlement_key || "";
    return key.startsWith("workout_groups.") && isCurrentlyActive(row);
  });

  const entitlementKeys = new Set(base.entitlements);
  activeEntitlements.forEach((row) => {
    const key = row?.entitlement_definitions?.entitlement_key;
    if (key) entitlementKeys.add(key);
  });

  const bestEntitlement = activeEntitlements
    .map((row) => ({ row, tier: row?.billing_products?.metadata?.tier || "free" }))
    .sort((a, b) => tierRank(b.tier) - tierRank(a.tier))[0];

  const activeSubscription = (subscriptions || []).find((row) => {
    const tier = row?.billing_products?.metadata?.tier;
    return tier && tierRank(tier) > 0;
  }) || null;

  const selectedProduct =
    bestEntitlement?.row?.billing_products ||
    activeSubscription?.billing_products ||
    (products || []).find((p) => p.product_key === "workout_groups_free");

  const tier = selectedProduct?.metadata?.tier || "free";
  const tierDefaults = TIER_LIMITS[tier] || FREE_LIMITS;
  const limits = selectedProduct?.metadata?.limits || tierDefaults;

  return {
    tier,
    planName: selectedProduct?.name || base.planName,
    source: bestEntitlement?.row?.source || ((selectedProduct?.metadata?.tier || "free") === "free" ? "free" : activeSubscription?.provider || "manual"),
    status: bestEntitlement?.row?.status || activeSubscription?.status || "active",
    expiresAt: bestEntitlement?.row?.expires_at || null,
    renewsAt: activeSubscription?.current_period_end || null,
    limits: {
      groups: typeof limits.groups === "number" ? limits.groups : tierDefaults.groups,
      athletes_per_group: typeof limits.athletes_per_group === "number" ? limits.athletes_per_group : tierDefaults.athletes_per_group,
      assigned_workouts_per_group: typeof limits.assigned_workouts_per_group === "number" ? limits.assigned_workouts_per_group : tierDefaults.assigned_workouts_per_group,
      ranges: Array.isArray(limits.ranges) ? limits.ranges : tierDefaults.ranges,
    },
    entitlements: entitlementKeys,
    products: products || [],
    prices: prices || [],
    activeEntitlements,
    activeSubscription,
  };
};

const accessFromOwnerFunctionData = (data, base) => {
  const tier = data?.tier || "free";
  const tierDefaults = TIER_LIMITS[tier] || FREE_LIMITS;
  const limits = data?.limits || tierDefaults;

  return {
    ...base,
    tier,
    planName: data?.planName || base.planName,
    source: data?.source || base.source,
    status: data?.status || base.status,
    expiresAt: data?.expiresAt || null,
    renewsAt: data?.renewsAt || null,
    limits: {
      groups: typeof limits.groups === "number" ? limits.groups : tierDefaults.groups,
      athletes_per_group: typeof limits.athletes_per_group === "number" ? limits.athletes_per_group : tierDefaults.athletes_per_group,
      assigned_workouts_per_group: typeof limits.assigned_workouts_per_group === "number" ? limits.assigned_workouts_per_group : tierDefaults.assigned_workouts_per_group,
      ranges: Array.isArray(limits.ranges) ? limits.ranges : tierDefaults.ranges,
    },
    entitlements: new Set([...(base.entitlements || []), ...(data?.entitlements || [])]),
  };
};

export const loadWorkoutGroupOwnerAccessForGroup = async (groupId) => {
  const base = getDefaultWorkoutGroupAccess();
  if (!groupId) return base;

  const { data, error } = await supabase.functions.invoke("workout_groups_owner_access", {
    body: { group_id: groupId },
  });

  if (error || data?.error) {
    throw new Error(data?.error || error?.message || "Could not load workout group owner access.");
  }

  return accessFromOwnerFunctionData(data, base);
};

export const loadWorkoutGroupOwnerAccessForJoinCode = async (joinCode) => {
  const base = getDefaultWorkoutGroupAccess();
  const cleanCode = String(joinCode || "").trim().toUpperCase();
  if (!cleanCode) return base;

  const { data, error } = await supabase.functions.invoke("workout_groups_owner_access", {
    body: { join_code: cleanCode },
  });

  if (error || data?.error) {
    throw new Error(data?.error || error?.message || "Could not load workout group owner access.");
  }

  return accessFromOwnerFunctionData(data, base);
};

export const loadWorkoutGroupOwnerAccessForClub = async (clubId) => {
  const base = getDefaultWorkoutGroupAccess();
  if (!clubId) return base;

  const { data, error } = await supabase.functions.invoke("workout_groups_owner_access", {
    body: { club_id: clubId },
  });

  if (error || data?.error) {
    throw new Error(data?.error || error?.message || "Could not load workout club owner access.");
  }

  return accessFromOwnerFunctionData(data, base);
};
